import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ContinuousReportingType } from '../ContinuousReporting';
import {
	DeliverableOrMilestoneType,
	getDeliverablesAndMilestonesByProjectIdAndStatementId,
	pickAllDeliverablesAndMilestonesByProjectIdAndStatementId,
	unpickAllDeliverablesAndMilestonesByProjectIdAndStatementId,
} from '@lib/api_ts/continuousReportingApi';
import { useLocation } from 'react-router-dom-v5-compat';
import AG_GRID_LOCALE_IT from 'components/ag-grid/locale-it';
import { ButtonToolbar, FormControl, InputGroup } from 'react-bootstrap';
import { translate } from 'components/translation/translation';
import { Lens } from 'icone';
import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import CustomPagination from 'components/atomic/organisms/ProjectReporting/CustomPagination';
import { debounce } from '@mui/material';
import { columnCentered } from 'lib/helpers/agGridHelpers';
import ContinuousReportingInfoCell from './ContinuousReportingInfoCell';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

interface ContinuousReportingDeliverablesAndMilestonesProps {
	data: ContinuousReportingType;
	isEnabledToView: boolean;
	state: 'DRAFT' | 'VALIDATED';
}

const ContinuousReportingDeliverablesAndMilestones: React.FC<ContinuousReportingDeliverablesAndMilestonesProps> = ({
	data,
	isEnabledToView,
	state,
}) => {
	const { pathname } = useLocation();
	const projectId = pathname.split('/')[2];
	const [filterInputValue, setFilterInputValue] = useState('');
	const [searchTerm, setSearchTerm] = useState('');
	const localeText = AG_GRID_LOCALE_IT;

	const gridRef = useRef(null);

	const [rowData, setRowData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [count, setCount] = useState<number>();
	const [currentPage, setCurrentPage] = useState(0);
	const [perPage, setPerPage] = useState(100);
	const [defaultPerPage, setDefaultPerPage] = useState(100);

	const [pickLoading, setPickLoading] = useState(false);

	const fetchData = useCallback(async () => {
		try {
			const { data: deliverablesAndMilestones, meta } = await getDeliverablesAndMilestonesByProjectIdAndStatementId(projectId, data.id, {
				currentPage: currentPage + 1,
				perPage,
				searchTerm,
			});
			const formattedData = deliverablesAndMilestones.map((item) => {
				return {
					id: item.id,
					type: item.type,
					beneficiaryName: item.responsibleContact ? item.responsibleContact.companies[0].name : '',
					description: item.description,
					forecastDate: item.forecastDate,
					effectiveDate: item.effectiveDate,
					note: item.note,
					checked: item.checked,
				};
			});
			setRowData(formattedData);
			setCount(meta.total);
			if (meta.perPage !== perPage) {
				setPerPage(meta.perPage);
				setDefaultPerPage(meta.perPage);
			}
		} catch (error) {
			console.log('error', error);
		} finally {
			setLoading(false);
		}
	}, [currentPage, perPage, searchTerm, projectId, data.id /*, state*/]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const columnDefs = [
		...(isEnabledToView && state === 'DRAFT'
			? [
					{
						headerName: '',
						maxWidth: 50,
						resizable: false,
						// headerCheckboxSelection: true,
						checkboxSelection: true,
						cellStyle: () => {
							//per disabilitare il click sulla riga in loading
							return loading || pickLoading ? { pointerEvents: 'none', opacity: '0.4' } : null;
						},
					},
				]
			: []),
		{
			headerName: 'Tipo',
			field: 'type',
			filter: 'agTextColumnFilter',
			valueGetter: (params: { data: DeliverableOrMilestoneType }) => {
				const selectedTask = params.data;
				if (!selectedTask.type) return '';
				let formattedType: string = selectedTask.type;
				if (formattedType === 'D') {
					formattedType = 'Deliverable';
				} else if (formattedType === 'M') {
					formattedType = 'Milestone';
				}
				return formattedType;
			},
		},

		{
			headerName: 'Beneficiario',
			field: 'beneficiaryName',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Descrizione',
			field: 'description',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Data prevista',
			field: 'forecastDate',
			filter: 'agTextColumnFilter',
			valueGetter: (params: { data: DeliverableOrMilestoneType }) => {
				const selectedTask = params.data;
				if (!selectedTask.forecastDate) return '';
				return format(selectedTask.forecastDate, 'dd/MM/yyyy', { locale: it });
			},
			...columnCentered(),
		},
		{
			headerName: 'Data effettiva',
			field: 'effectiveDate',
			filter: 'agTextColumnFilter',
			valueGetter: (params: { data: DeliverableOrMilestoneType }) => {
				const selectedTask = params.data;
				if (!selectedTask.effectiveDate) return '';
				return format(selectedTask.effectiveDate, 'dd/MM/yyyy', { locale: it });
			},
			...columnCentered(),
		},
		{
			headerName: 'Note',
			field: 'note',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Info',
			field: 'id',
			cellRenderer: ContinuousReportingInfoCell,
			minWidth: 90,
			maxWidth: 90,
			...columnCentered(),
		},
	];

	const defaultColDef = {
		resizable: false,
		wrapText: true,
		// autoHeight: true,
		flex: 1,
		suppressMovable: true,
		cellStyle: {
			padding: '0 1rem',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
		},
	};

	const debouncedSetSearchTermRef = useRef(
		debounce((value) => {
			setCurrentPage(0);
			setPerPage(defaultPerPage);
			if (gridRef.current) {
				gridRef.current.api.paginationGoToPage(0);
				gridRef.current.api.paginationSetPageSize(defaultPerPage);
			}
			setSearchTerm(value); // Solo l'aggiornamento finale dopo debounce
		}, 500),
	);
	const onFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setFilterInputValue(value);
	};

	useEffect(() => {
		debouncedSetSearchTermRef.current(filterInputValue);
	}, [filterInputValue]);

	const handleData = (params: {
		api: {
			forEachNode: (
				arg0: (node: { setSelected: (arg0: boolean, arg1: boolean, arg2: boolean) => void; data: { checked: number } }) => void,
			) => void;
		};
	}) => {
		params.api.forEachNode((node) => {
			node.setSelected(node.data.checked === 1, false, true);
		});
	};

	const pickAll = async (deliverableIds: number[], milestoneIds: number[]) => {
		try {
			await pickAllDeliverablesAndMilestonesByProjectIdAndStatementId(projectId, data.id, deliverableIds, milestoneIds);
		} catch (error) {
			console.log('error', error);
		}
	};
	const unpickAll = async (deliverableIds: number[], milestoneIds: number[]) => {
		try {
			await unpickAllDeliverablesAndMilestonesByProjectIdAndStatementId(projectId, data.id, deliverableIds, milestoneIds);
		} catch (error) {
			console.log('error', error);
		}
	};

	return (
		<>
			<div
				className="mb-3"
				style={{
					display: 'flex',
					justifyContent: 'flex-end',
					alignItems: 'center',
				}}
			>
				<ButtonToolbar className="justify-content-end">
					<InputGroup className="search-table align-items-center mt-md-0 ms-1" style={{ width: '300px' }}>
						<FormControl
							type="text"
							className="filter-text-box"
							placeholder={translate('common.search', 'Cerca') + '...'}
							value={filterInputValue}
							onChange={onFilterChange}
						/>
						<div style={{ width: '40px', textAlign: 'center' }}>
							<Lens className="lens" />
						</div>
					</InputGroup>
				</ButtonToolbar>
			</div>
			<div className="ag-theme-alpine list-table relative">
				<AgGridReact
					ref={gridRef}
					rowData={rowData}
					columnDefs={[...columnDefs]}
					defaultColDef={defaultColDef}
					domLayout="autoHeight"
					tooltipShowDelay={0}
					tooltipHideDelay={2000}
					tooltipMouseTrack={true}
					headerHeight={50}
					rowHeight={50}
					suppressCellFocus={true}
					localeText={localeText}
					// rowSelection="multiple"
					// rowMultiSelectWithClick={true}
					onFirstDataRendered={handleData}
					onGridReady={handleData}
					suppressRowClickSelection={true}
					onSelectionChanged={(event) => {
						(async () => {
							setPickLoading(true);
							// Ottieni tutte le righe renderizzate nella griglia
							const allRows: DeliverableOrMilestoneType[] = event.api.getRenderedNodes().map((node) => node.data);

							// Ottieni le righe selezionate
							const selectedRows: DeliverableOrMilestoneType[] = event.api.getSelectedRows();

							// Crea un set degli ID delle righe selezionate per una ricerca efficiente
							const selectedIds = new Set(selectedRows.map((row) => `${row.type}-${row.id}`));

							// Determina le righe non selezionate confrontando tutte le righe con quelle selezionate
							const unselectedRows = allRows.filter((row) => !selectedIds.has(`${row.type}-${row.id}`));

							// Separa gli ID dei deliverable e dei milestone tra le righe selezionate
							const deliverableIdsToPick = selectedRows.filter((row) => row.type === 'D').map((row) => row.id);

							const milestoneIdsToPick = selectedRows.filter((row) => row.type === 'M').map((row) => row.id);

							// Separa gli ID dei deliverable e dei milestone tra le righe non selezionate
							const deliverableIdsToUnpick = unselectedRows.filter((row) => row.type === 'D').map((row) => row.id);

							const milestoneIdsToUnpick = unselectedRows.filter((row) => row.type === 'M').map((row) => row.id);

							await pickAll(deliverableIdsToPick, milestoneIdsToPick);
							await unpickAll(deliverableIdsToUnpick, milestoneIdsToUnpick);

							setPickLoading(false);
							// refreshAmounts();
						})();
					}}
					onPaginationChanged={(event) => {
						if (event.newData === undefined) return;
						handleData(event);
					}}
				/>
			</div>
			<CustomPagination
				count={count || 100}
				loading={loading}
				currentPage={currentPage || 0}
				setCurrentPage={setCurrentPage}
				perPage={perPage || 100}
				setPerPage={setPerPage}
				gridRef={gridRef}
			/>
		</>
	);
};

export default ContinuousReportingDeliverablesAndMilestones;
